import React from "react";
import { useAnimatedNumber } from "@/lib/useAnimatedNumber";

/**
 * Big circular risk score ring.
 * `score` 0-100, lower is better (less risk).
 */
export default function RiskRing({ score = 0, inherent = 0, reduction = 0, size = 200 }) {
  const animated = useAnimatedNumber(score, 1200);
  const animatedReduction = useAnimatedNumber(reduction, 1200);
  const radius = (size - 24) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(100, Math.max(0, animated)) / 100;
  const offset = circumference * (1 - pct);

  // Color: green if low, amber mid, red high
  let color = "#16A34A";
  if (score >= 65) color = "#DC2626";
  else if (score >= 35) color = "#D97706";

  let band = "Low Residual Risk";
  if (score >= 65) band = "High Residual Risk";
  else if (score >= 35) band = "Moderate Residual Risk";

  return (
    <div className="relative inline-flex flex-col items-center" data-testid="risk-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E2E8F0"
          strokeWidth="14"
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="14"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke 0.4s" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Residual Risk</div>
        <div className="text-5xl font-bold" style={{ fontFamily: "Geist", color }}>
          {Math.round(animated)}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">of 100</div>
      </div>
      <div className="mt-3 text-center">
        <div className="text-sm font-semibold text-slate-800">{band}</div>
        {inherent > 0 && (
          <div className="text-xs text-emerald-700 font-medium mt-0.5">
            ↓ {animatedReduction.toFixed(1)}% reduced from inherent {inherent.toFixed(0)}
          </div>
        )}
      </div>
    </div>
  );
}
